import { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/lib/api-client';

// Posts the page height to the website framing us, so embed.js can size the
// iframe to its content (no scroll inside a scroll).
export function useEmbedHeight(enabled: boolean) {
  useEffect(() => {
    if (!enabled || window.parent === window) return;
    let last = 0;
    const send = () => {
      const height = Math.ceil(document.documentElement.scrollHeight);
      if (height === last) return;
      last = height;
      window.parent.postMessage({ type: 'sloty:height', height }, '*');
    };
    const observer = new ResizeObserver(send);
    observer.observe(document.body);
    send();
    return () => observer.disconnect();
  }, [enabled]);
}

// The origin of the website showing us in an iframe, if any.
export function parentOrigin(): string | null {
  if (window.parent === window) return null;
  const ancestors = (window.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
  if (ancestors && ancestors.length > 0) return ancestors[0];
  try {
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    return null;
  }
}

export type EmbedAccess = 'checking' | 'allowed' | 'blocked';

// Asks the API whether the framing website is on the org's allowed list. The
// list itself stays private. Our own origin (admin live preview) and direct
// visits are always allowed.
export function useEmbedAccess(slug: string | undefined, enabled: boolean): EmbedAccess {
  const [access, setAccess] = useState<EmbedAccess>(enabled ? 'checking' : 'allowed');

  useEffect(() => {
    if (!enabled || !slug) return;
    const origin = parentOrigin();
    if (!origin || origin === window.location.origin) {
      setAccess('allowed');
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/v1/organizations/${slug}/embed_check?origin=${encodeURIComponent(origin)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setAccess(body?.data?.allowed === false ? 'blocked' : 'allowed'); })
      .catch(() => { if (!cancelled) setAccess('allowed'); });
    return () => { cancelled = true; };
  }, [slug, enabled]);

  return access;
}
